import { Stack, Text, useTheme } from '@ltvco/refresh-lib/theme';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import { DirectionsCar, Email, Home, Person, Phone } from '@mui/icons-material';
import { useContext, useEffect, useRef } from 'react';

import {
  BrandLogoChat,
  ChatChips,
  ChatTerms,
  LandingTextSectionStack,
} from 'components/ChatBV/components';
import {
  PLACEHOLDER_COLLECTION,
  SEARCH_TYPES,
} from 'components/ChatBV/constants';
import { ChipData } from 'components/ChatBV/types';

import { DeepSearchContext } from '../context';

import { A2uiReportRenderer } from './A2uiReportRenderer';
import {
  DeepSearchChatInput,
  DeepSearchChatInputRef,
} from './DeepSearchChatInput';
import {
  MessagesArea,
  MessageBubble,
  TypingIndicator,
  PageContainer,
} from './DeepSearchPage.styles';

const CHIPS: ChipData[] = [
  {
    icon: <Person sx={{ color: 'primary.main', fontSize: '16px' }} />,
    title: 'Find a person',
    type: SEARCH_TYPES.PERSON,
  },
  {
    icon: <Phone sx={{ color: 'primary.main', fontSize: '16px' }} />,
    title: 'Search a phone',
    type: SEARCH_TYPES.PHONE,
  },
  {
    icon: <Home sx={{ color: 'primary.main', fontSize: '16px' }} />,
    title: 'View address info',
    type: SEARCH_TYPES.PROPERTY,
  },
  {
    icon: <Email sx={{ color: 'primary.main', fontSize: '16px' }} />,
    title: 'Check an email',
    type: SEARCH_TYPES.EMAIL,
  },
  {
    icon: <DirectionsCar sx={{ color: 'primary.main', fontSize: '20px' }} />,
    title: 'Search a vehicle',
    type: SEARCH_TYPES.VEHICLE,
  },
];

export const DeepSearchPage = () => {
  const { activeMessages: messages, isLoading } = useContext(DeepSearchContext);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<DeepSearchChatInputRef>(null);
  const theme = useTheme();
  const { isMobile } = useScreenSize();

  const showLanding = messages.length === 0;

  useEffect(() => {
    document.body.classList.add('deepsearch-page');
    return () => {
      document.body.classList.remove('deepsearch-page');
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const handleChipClick = (type: string) => {
    const item = PLACEHOLDER_COLLECTION.find((p) => p.searchType === type);
    if (item) {
      chatInputRef.current?.setValue(item.chipMainText);
    }
  };

  if (showLanding) {
    return (
      <PageContainer>
        <Stack
          flexDirection="column"
          justifyContent="space-between"
          width="100%"
          flex={1}
        >
          <Stack
            flexDirection="column"
            alignItems={isMobile ? 'flex-start' : 'center'}
            paddingTop={'10%'}
            width="100%"
            px={{ xs: '15px', md: '30px' }}
          >
            <Stack width="100%" alignItems={isMobile ? 'flex-start' : 'center'}>
              <BrandLogoChat
                width={isMobile ? '100px' : '138px'}
                height={isMobile ? '16px' : '21px'}
                sx={{ margin: '20px 0' }}
              />
              <LandingTextSectionStack
                flexDirection="row"
                fontSize="2.625rem"
                fontWeight="bold"
                m={0}
                mb={2.25}
                flexWrap="nowrap"
                alignItems={isMobile ? 'start' : 'center'}
              >
                <Text
                  fontSize="inherit"
                  fontWeight="bold"
                  m={0}
                  mr={0.5}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Ask More.
                </Text>
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{
                    [theme.breakpoints.down('md')]: { ml: 0, mt: 0.5 },
                  }}
                >
                  <Text
                    fontSize="inherit"
                    fontWeight="bold"
                    m={0}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Discover{' '}
                  </Text>
                  <Text
                    fontSize="inherit"
                    fontWeight="bold"
                    m={0}
                    color="primary.main"
                    sx={{ whiteSpace: 'nowrap' }}
                    ml={0.5}
                  >
                    More
                  </Text>
                </Stack>
              </LandingTextSectionStack>
              <Text variant="body1" mb={isMobile ? 4.5 : 6}>
                Your AI assistant for in-depth searches on people, phones,
                addresses, emails, and vehicles.
              </Text>
            </Stack>

            <DeepSearchChatInput ref={chatInputRef} isLanding />

            <Stack
              width="100%"
              maxWidth="100%"
              margin="0 auto"
              sx={{
                [theme.breakpoints.up('md')]: { width: '100%' },
                [theme.breakpoints.down('md')]: { width: '90%' },
              }}
            >
              <ChatChips chips={CHIPS} onChipClick={handleChipClick} />
            </Stack>
          </Stack>

          <Stack
            sx={{
              width: '100%',
              '& [data-terms-element]': { marginTop: '0 !important' },
            }}
          >
            <ChatTerms isLanding />
          </Stack>
        </Stack>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <MessagesArea>
        {messages.map((message) => {
          if (Array.isArray(message.data)) {
            return (
              <A2uiReportRenderer key={message.id} messages={message.data} />
            );
          }
          return (
            <MessageBubble key={message.id} isUser={message.role === 'user'}>
              {message.data}
            </MessageBubble>
          );
        })}
        {isLoading && (
          <TypingIndicator>
            <span />
            <span />
            <span />
          </TypingIndicator>
        )}
        <div ref={messagesEndRef} />
      </MessagesArea>
      <DeepSearchChatInput />
    </PageContainer>
  );
};
